/**
 * DonutChart — SVG ring chart with center label + legend rows.
 * Used by Publisher "Portfolio Breakdown" card.
 *
 * Flow: compute arc lengths from values → draw stroke-dasharray circles (stacked) →
 *   render center text → render legend below.
 * Donut rings are rotational — no RTL axis adaptation needed.
 */
import { useLocale } from "@/shared/hooks/useLocale";

export interface DonutSegment {
  label: string;
  count: number;
  color: string;
}

interface DonutChartProps {
  segments: DonutSegment[];
  total: number;
  centerLabel?: string;
  centerSub?: string;
  size?: number;
  strokeWidth?: number;
}

export function DonutChart({
  segments,
  total,
  centerLabel,
  centerSub,
  size = 128,
  strokeWidth = 14,
}: DonutChartProps) {
  const { fmtNum } = useLocale();
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - strokeWidth * 2) / 2;
  const C = 2 * Math.PI * r;

  return (
    <div className="flex flex-col items-center gap-4">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Track */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--color-surface-low)" strokeWidth={strokeWidth} />

        {/* Segments */}
        {segments.map((seg, i) => {
          const len = (seg.count / (total || 1)) * C;
          const gap = C - len;
          // Cumulative length of preceding segments — computed without mutating
          // render-scoped state (segment counts are small, so the prefix sum is cheap).
          const offset = segments
            .slice(0, i)
            .reduce((acc, s) => acc + (s.count / (total || 1)) * C, 0);
          const dashOffset = C / 4 - offset;
          return (
            <circle
              key={seg.label}
              cx={cx} cy={cy} r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${len.toFixed(2)} ${gap.toFixed(2)}`}
              strokeDashoffset={dashOffset.toFixed(2)}
              strokeLinecap="round"
              transform={`rotate(-90,${cx},${cy})`}
              opacity="0.85"
            />
          );
        })}

        {/* Center label */}
        {centerLabel && (
          <text
            x={cx} y={cy - 2}
            textAnchor="middle"
            fontSize="22" fontWeight="800"
            fill="var(--text-primary)" fontFamily="var(--font-sans)"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {centerLabel}
          </text>
        )}
        {centerSub && (
          <text
            x={cx} y={cy + 14}
            textAnchor="middle"
            fontSize="8" fontWeight="700"
            fill="var(--text-tertiary)" fontFamily="var(--font-sans)"
            letterSpacing="0.1em"
          >
            {centerSub}
          </text>
        )}
      </svg>

      {/* Legend */}
      <div className="flex flex-col gap-2 w-full">
        {segments.map((seg) => {
          const pct = Math.round((seg.count / (total || 1)) * 100);
          return (
            <div key={seg.label} className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-[3px] shrink-0" style={{ background: seg.color, opacity: 0.85 }} />
                <span className="text-[12px] font-medium" style={{ color: "var(--text-secondary, rgba(26,25,23,0.75))" }}>
                  {seg.label}
                </span>
              </div>
              <div>
                <span className="text-[13px] font-bold" style={{ color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}>
                  {fmtNum(seg.count)}
                </span>
                <span className="text-[11px] ms-1" style={{ color: "var(--text-tertiary)" }}>
                  {fmtNum(pct)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
